class Optimal7OrderPortal extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.rows = Array.from(this.querySelectorAll('[data-order-row]'));
    this.search = this.querySelector('[data-order-search]');
    this.bar = this.querySelector('[data-order-bar]');
    this.itemCount = this.querySelector('[data-order-item-count]');
    this.subtotal = this.querySelector('[data-order-subtotal]');
    this.submit = this.querySelector('[data-order-submit]');
    this.status = this.querySelector('[data-order-status]');
    this.dialog = this.querySelector('[data-order-dialog]');
    this.dialogBody = this.querySelector('[data-dialog-body]');
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.addUrl = this.dataset.addUrl || '/cart/add.js';

    this.rows.forEach((row) => {
      row._priceBreaks = this.parsePriceBreaks(row);
      const input = row.querySelector('[data-qty-input]');
      const minus = row.querySelector('[data-qty-minus]');
      const plus = row.querySelector('[data-qty-plus]');

      minus?.addEventListener('click', () => {
        const current = parseInt(input.value, 10) || 0;
        this.setRowQty(row, current - 1);
      });
      plus?.addEventListener('click', () => {
        const current = parseInt(input.value, 10) || 0;
        const limit = this.stockLimit(row);
        if (limit !== null && current >= limit) {
          this.setRowQty(row, current, true);
          return;
        }
        this.setRowQty(row, current + 1);
      });
      input?.addEventListener('change', () => this.setRowQty(row, input.value));
      input?.addEventListener('input', () => this.setRowQty(row, input.value));

      row.querySelectorAll('[data-quick-view-open]').forEach((btn) => {
        btn.addEventListener('click', () => this.openQuickView(row));
      });
    });

    if (this.search) {
      const isolate = (event) => event.stopPropagation();
      this.search.closest('.o7-order__search')?.addEventListener('click', isolate);
      this.search.addEventListener('click', isolate);
      this.search.addEventListener('focusin', isolate);
      this.search.addEventListener('keydown', (event) => {
        event.stopPropagation();
        if (event.key === 'Enter') event.preventDefault();
      });
      this.search.addEventListener('input', () => this.filterRows());
    }
    this.submit?.addEventListener('click', () => this.addSelected());
    this.querySelector('[data-dialog-close]')?.addEventListener('click', () => this.closeQuickView());
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.closeQuickView();
    });
    this.dialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeQuickView();
    });

    this.updateSummary();
    this.loadCartQuantities();
  }

  stockLimit(row) {
    if (row.dataset.inventoryTracked !== 'true') return null;
    const stock = Math.max(0, parseInt(row.dataset.inventory, 10) || 0);
    const inCart = Math.max(0, parseInt(row.dataset.cartQty, 10) || 0);
    return Math.max(0, stock - inCart);
  }

  setRowQty(row, raw, blocked) {
    const input = row.querySelector('[data-qty-input]');
    const limit = this.stockLimit(row);
    let qty = Math.max(0, parseInt(raw, 10) || 0);
    let capped = Boolean(blocked);
    if (limit !== null && qty > limit) {
      qty = limit;
      capped = true;
    }
    if (input) input.value = String(qty);
    this.syncRowPricing(row, qty);
    this.updateSummary();
    this.syncStockNote(row, capped);
  }

  syncStockNote(row, capped) {
    const note = row.querySelector('[data-stock-note]');
    const input = row.querySelector('[data-qty-input]');
    const plus = row.querySelector('[data-qty-plus]');
    const limit = this.stockLimit(row);
    if (limit === null) {
      if (plus) plus.disabled = false;
      if (input) input.disabled = false;
      if (note) note.hidden = true;
      return;
    }
    const qty = Math.max(0, parseInt(input?.value, 10) || 0);
    const stock = Math.max(0, parseInt(row.dataset.inventory, 10) || 0);
    const inCart = Math.max(0, parseInt(row.dataset.cartQty, 10) || 0);
    if (input) {
      input.max = String(limit);
      input.disabled = limit === 0;
    }
    if (plus) plus.disabled = qty >= limit;
    if (!note) return;
    if (limit === 0) {
      note.hidden = false;
      note.textContent = inCart > 0 && stock > 0
        ? `Only ${stock} in stock, already in your cart`
        : 'Sold out';
      return;
    }
    if (capped) {
      note.hidden = false;
      note.textContent = inCart > 0
        ? `Only ${stock} in stock. You can add ${limit} more.`
        : `Only ${stock} in stock`;
      return;
    }
    note.hidden = true;
    note.textContent = '';
  }

  async loadCartQuantities() {
    const cartUrl = `${(this.cartUrl || '/cart').replace(/\/$/, '')}.js`;
    try {
      const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const counts = {};
      (cart.items || []).forEach((item) => {
        const id = String(item.variant_id);
        counts[id] = (counts[id] || 0) + item.quantity;
      });
      this.rows.forEach((row) => {
        row.dataset.cartQty = String(counts[row.dataset.variantId] || 0);
        const input = row.querySelector('[data-qty-input]');
        const current = parseInt(input?.value, 10) || 0;
        const limit = this.stockLimit(row);
        this.setRowQty(row, current, limit !== null && current > limit);
      });
    } catch (error) {
      this.rows.forEach((row) => this.syncStockNote(row, false));
    }
  }

  parsePriceBreaks(row) {
    const script = row.querySelector('[data-price-breaks]');
    if (!script) return [{ min: 1, cents: 0 }];
    try {
      const parsed = JSON.parse(script.textContent.trim());
      if (!Array.isArray(parsed) || !parsed.length) return [{ min: 1, cents: 0 }];
      return parsed
        .map((item) => ({
          min: Number(item.min) || 1,
          cents: Number(item.cents) || 0,
        }))
        .sort((a, b) => a.min - b.min);
    } catch (error) {
      return [{ min: 1, cents: 0 }];
    }
  }

  unitCentsForQty(qty, breaks) {
    if (!qty || !breaks?.length) return breaks?.[0]?.cents || 0;
    let unit = breaks[0].cents;
    for (const tier of breaks) {
      if (qty >= tier.min) unit = tier.cents;
    }
    return unit;
  }

  lineCentsForQty(qty, breaks) {
    return this.unitCentsForQty(qty, breaks) * qty;
  }

  syncRowPricing(row, qty) {
    const breaks = row._priceBreaks || [{ min: 1, cents: 0 }];
    const unitCents = this.unitCentsForQty(Math.max(qty, 1), breaks);
    const lineCents = qty > 0 ? this.lineCentsForQty(qty, breaks) : 0;
    const unitLabel = this.formatMoney(unitCents / 100);

    row.querySelectorAll('[data-unit-price]').forEach((el) => {
      el.textContent = unitLabel;
    });

    const lineTotal = row.querySelector('[data-line-total]');
    if (lineTotal) {
      lineTotal.textContent = qty > 0 ? this.formatMoney(lineCents / 100) : '';
    }
  }

  filterRows() {
    const q = (this.search?.value || '').trim().toLowerCase();
    this.rows.forEach((row) => {
      const hay = (row.dataset.search || '').toLowerCase();
      row.hidden = q !== '' && !hay.includes(q);
    });
  }

  selectedItems() {
    return this.rows
      .map((row) => {
        const input = row.querySelector('[data-qty-input]');
        let qty = Math.max(0, parseInt(input?.value, 10) || 0);
        const limit = this.stockLimit(row);
        if (limit !== null) qty = Math.min(qty, limit);
        if (!qty) return null;
        const breaks = row._priceBreaks || [{ min: 1, cents: 0 }];
        return {
          id: Number(row.dataset.variantId),
          quantity: qty,
          lineCents: this.lineCentsForQty(qty, breaks),
        };
      })
      .filter(Boolean);
  }

  updateSummary() {
    const items = this.selectedItems();
    const units = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalCents = items.reduce((sum, item) => sum + item.lineCents, 0);

    if (this.itemCount) {
      this.itemCount.textContent =
        units === 1 ? '1 unit selected' : `${units} units selected`;
    }
    if (this.subtotal) {
      this.subtotal.textContent = this.formatMoney(totalCents / 100);
    }
    if (this.submit) {
      this.submit.disabled = units === 0;
    }
    this.bar?.classList.toggle('is-visible', units > 0);
  }

  openQuickView(row) {
    const template = row.querySelector('[data-qv-template]');
    if (!template || !this.dialog || !this.dialogBody) return;
    this.dialogBody.replaceChildren(template.content.cloneNode(true));
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('o7-order-qv-open');
  }

  closeQuickView() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
    if (this.dialogBody) this.dialogBody.innerHTML = '';
    document.documentElement.classList.remove('o7-order-qv-open');
  }

  formatMoney(amountMajor) {
    const amount = Number(amountMajor || 0);
    const formatted = amount.toFixed(2);
    if (this.moneyFormat.includes('{{amount}}')) {
      return this.moneyFormat.replace('{{amount}}', formatted);
    }
    if (this.moneyFormat.includes('{{amount_no_decimals}}')) {
      return this.moneyFormat.replace('{{amount_no_decimals}}', String(Math.round(amount)));
    }
    return `$${formatted}`;
  }

  setStatus(message, type) {
    if (!this.status) return;
    if (!message) {
      this.status.hidden = true;
      this.status.textContent = '';
      this.status.className = 'o7-order__status';
      return;
    }
    this.status.hidden = false;
    this.status.textContent = message;
    this.status.className = `o7-order__status o7-order__status--${type || 'error'}`;
    if (type !== 'success') {
      this.status.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  async addSelected() {
    const items = this.selectedItems().map(({ id, quantity }) => ({ id, quantity }));
    if (!items.length) return;

    this.submit.disabled = true;
    this.submit.textContent = this.dataset.addingLabel || 'Adding…';
    this.setStatus('');

    try {
      const response = await fetch(this.addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add items to cart.');
      }

      this.setStatus(this.dataset.successLabel || 'Added to cart.', 'success');
      window.location.href = this.cartUrl;
    } catch (error) {
      this.setStatus(error.message || 'Could not add items to cart.', 'error');
      this.submit.disabled = false;
      this.submit.textContent = this.dataset.submitLabel || 'Add to cart';
    }
  }
}

if (!customElements.get('optimal7-order-portal')) {
  customElements.define('optimal7-order-portal', Optimal7OrderPortal);
}

(() => {
  if (window.O7OrderBrochure) return;
  window.O7OrderBrochure = true;

  const brochureDialog = () => document.querySelector('[data-brochure-dialog]');

  const closeBrochure = () => {
    const dialog = brochureDialog();
    if (!dialog) return;
    const frame = dialog.querySelector('[data-brochure-frame]');
    if (dialog.open && typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    if (frame) frame.removeAttribute('src');
  };

  const openBrochure = (link) => {
    const dialog = brochureDialog();
    if (!dialog) return;
    const pdf = link.getAttribute('data-pdf') || link.getAttribute('href');
    if (!pdf || pdf === '#') return;
    const frame = dialog.querySelector('[data-brochure-frame]');
    const title = dialog.querySelector('[data-brochure-title]');
    const fallback = dialog.querySelector('[data-brochure-fallback]');
    if (title) title.textContent = link.getAttribute('data-title') || 'PDF';
    if (fallback) fallback.href = pdf;
    if (frame) frame.src = pdf;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-brochure-open]');
    if (opener) {
      event.preventDefault();
      openBrochure(opener);
      return;
    }
    const closer = event.target.closest('[data-brochure-close]');
    if (closer) {
      event.preventDefault();
      closeBrochure();
      return;
    }
    const dialog = event.target.closest('[data-brochure-dialog]');
    if (dialog && event.target === dialog) closeBrochure();
  });

  document.addEventListener('cancel', (event) => {
    if (!event.target.matches || !event.target.matches('[data-brochure-dialog]')) return;
    event.preventDefault();
    closeBrochure();
  }, true);
})();
